import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowCounterclockwise, BsPlayCircle } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { formatDate } from '#lib/dateUtils';
import { useErrorStore } from '#lib/stores/errorStore';

import { useResumeSubscription, useRevertCancellation } from '../../../../models/billing/mutations';
import { canOfferPause } from '../../../../utils/subscriptionState';

const PERIOD_END_FALLBACK = 'zum Ende der aktuellen Abrechnungsperiode';

function buildNoticeCopy({ view, currentPeriodEnd, pause, isPaused }) {
	const endDate = currentPeriodEnd ? formatDate(currentPeriodEnd) : null;
	const startsAt = pause?.startsAt ? formatDate(pause.startsAt) : null;
	const resumesAt = pause?.resumesAt ? formatDate(pause.resumesAt) : null;

	if (view === 'cancelling') {
		return {
			tone: 'caution',
			title: endDate ? `Gekündigt zum ${endDate}` : 'Gekündigt zum Ende der Abrechnungsperiode',
			body: isPaused
				? 'Lead-Recherche, Versand und Export bleiben bis dahin deaktiviert. Ihre Daten bleiben auch danach erhalten.'
				: 'Bis dahin nutzen Sie B2B Hero uneingeschränkt. Ihre Daten bleiben auch danach erhalten.',
		};
	}

	if (view === 'pauseScheduled') {
		return {
			tone: 'info',
			title: `Pause ab ${startsAt ?? 'Periodenende'}`,
			body: resumesAt
				? `Ihr Abonnement pausiert vom ${startsAt} bis zum ${resumesAt}. Bis zum ${startsAt} nutzen Sie B2B Hero wie gewohnt.`
				: `Bis zum ${startsAt} nutzen Sie B2B Hero wie gewohnt.`,
		};
	}

	return {
		tone: 'info',
		title: resumesAt ? `Pausiert bis ${resumesAt}` : 'Abonnement pausiert',
		body: resumesAt
			? 'Ihre Daten bleiben sichtbar; Lead-Recherche, Versand und Export sind bis dahin deaktiviert. Danach läuft Ihr Abo automatisch weiter.'
			: 'Ihre Daten bleiben sichtbar; Lead-Recherche, Versand und Export sind während der Pause deaktiviert.',
	};
}

function buildResumeCopy({ view, currentPeriodEnd }) {
	if (view === 'paused') {
		const nextInvoice = currentPeriodEnd ? `zum ${formatDate(currentPeriodEnd)}` : PERIOD_END_FALLBACK;
		return {
			title: 'Abonnement fortsetzen',
			message: `Möchten Sie Ihr Abonnement jetzt fortsetzen? Alle Funktionen werden wieder freigeschaltet; die nächste Rechnung erhalten Sie ${nextInvoice}.`,
			confirmText: 'Jetzt fortsetzen',
			successTitle: 'Abonnement fortgesetzt',
			successMessage: 'Alle Funktionen sind wieder freigeschaltet.',
		};
	}

	return {
		title: 'Pause aufheben',
		message: 'Möchten Sie die geplante Pause aufheben? Ihr Abonnement läuft dann ohne Unterbrechung weiter.',
		confirmText: 'Pause aufheben',
		successTitle: 'Pause aufgehoben',
		successMessage: 'Ihr Abonnement läuft ohne Unterbrechung weiter.',
	};
}

// Reverting only clears the cancellation; a scheduled or running pause window stays in place.
function buildRevertCopy({ currentPeriodEnd, pause, isPaused }) {
	const question = 'Möchten Sie Ihre Kündigung zurücknehmen?';

	if (isPaused) {
		const until = pause?.resumesAt ? ` bis zum ${formatDate(pause.resumesAt)}` : '';
		return {
			message: `${question} Ihre Pause bleibt${until} bestehen; danach läuft Ihr Abonnement automatisch weiter.`,
			successMessage: 'Ihre Pause bleibt bestehen.',
		};
	}

	if (pause) {
		return {
			message: `${question} Ihre geplante Pause ab dem ${formatDate(pause.startsAt)} bleibt bestehen.`,
			successMessage: 'Ihre geplante Pause bleibt bestehen.',
		};
	}

	const renewalText = currentPeriodEnd ? `am ${formatDate(currentPeriodEnd)}` : PERIOD_END_FALLBACK;
	return {
		message: `${question} Ihr Abonnement läuft dann wie gewohnt weiter und verlängert sich ${renewalText} regulär.`,
		successMessage: 'Ihr Abonnement läuft wie gewohnt weiter.',
	};
}

export const SubscriptionStatusNotice = ({ subscription, view, isPaused = false, onCancelAnyway, onPauseInstead }) => {
	const { showSuccess } = useErrorStore();
	const revertCancellation = useRevertCancellation();
	const resumeSubscription = useResumeSubscription();
	const [confirm, setConfirm] = useState(null);

	const { currentPeriodEnd, pause, retention } = subscription;
	const notice = buildNoticeCopy({ view, currentPeriodEnd, pause, isPaused });
	const resumeCopy = buildResumeCopy({ view, currentPeriodEnd });
	const revertCopy = buildRevertCopy({ currentPeriodEnd, pause, isPaused });

	const handleRevert = async () => {
		await revertCancellation.mutateAsync();
		showSuccess({ title: 'Kündigung zurückgenommen', message: revertCopy.successMessage });
	};

	const handleResume = async () => {
		await resumeSubscription.mutateAsync();
		showSuccess({ title: resumeCopy.successTitle, message: resumeCopy.successMessage });
	};

	return (
		<Alert tone={notice.tone} title={notice.title}>
			<p>{notice.body}</p>

			<div className="mt-4 flex flex-wrap gap-3">
				{view === 'cancelling' ? (
					<>
						<Button variant="solid" onClick={() => setConfirm('revert')}>
							Kündigung zurücknehmen
						</Button>
						{canOfferPause(retention) && (
							<Button variant="outline" onClick={onPauseInstead}>
								Stattdessen pausieren
							</Button>
						)}
					</>
				) : (
					<>
						{retention?.canResume && (
							<Button
								variant={view === 'paused' ? 'solid' : 'outline'}
								onClick={() => setConfirm('resume')}
							>
								{resumeCopy.confirmText}
							</Button>
						)}
						<Button variant="danger" onClick={onCancelAnyway}>
							Trotzdem kündigen
						</Button>
					</>
				)}
			</div>

			<ConfirmModal
				isOpen={confirm === 'revert'}
				onClose={() => setConfirm(null)}
				onConfirm={handleRevert}
				title="Kündigung zurücknehmen"
				message={revertCopy.message}
				subtitle={null}
				confirmText="Kündigung zurücknehmen"
				cancelText="Abbrechen"
				color="primary"
				icon={BsArrowCounterclockwise}
				isLoading={revertCancellation.isPending}
			/>

			<ConfirmModal
				isOpen={confirm === 'resume'}
				onClose={() => setConfirm(null)}
				onConfirm={handleResume}
				title={resumeCopy.title}
				message={resumeCopy.message}
				subtitle={null}
				confirmText={resumeCopy.confirmText}
				cancelText="Abbrechen"
				color="primary"
				icon={BsPlayCircle}
				isLoading={resumeSubscription.isPending}
			/>
		</Alert>
	);
};

SubscriptionStatusNotice.propTypes = {
	subscription: PropTypes.shape({
		currentPeriodEnd: PropTypes.string,
		pause: PropTypes.shape({
			startsAt: PropTypes.string.isRequired,
			resumesAt: PropTypes.string,
		}),
		retention: PropTypes.shape({
			canPause: PropTypes.bool,
			canResume: PropTypes.bool,
			pauseOptions: PropTypes.array,
		}),
	}).isRequired,
	view: PropTypes.oneOf(['cancelling', 'pauseScheduled', 'paused']).isRequired,
	isPaused: PropTypes.bool,
	onCancelAnyway: PropTypes.func.isRequired,
	onPauseInstead: PropTypes.func.isRequired,
};
